/**
 * Main-page capsules — pure mappers over the `GET /capsules*` family
 * (PLAYGROUND_CHANGES §2.5–§2.5c, FE_CONTRACT_CHANGES 2026-09-10).
 *
 * The backend answers flat `outfits[].items[]` with `type_id` /
 * `main_category_id`; the cards want a named slot. The slot vocabulary below
 * is the ML `type` table (0 top … 5 outwear) plus the bag family of
 * `main_category` ids that `domain/outfit_composition.py` pins by name.
 */

import type { Audience } from '@/helpers/audience'
import { resolveAudience } from '@/helpers/audience'
import type { FairCapsuleItem, FairCapsuleItemType } from '@/ui/tokens/fair-capsule.data'
import type { Capsule, CapsuleData, CapsuleItem } from '@/types/capsule.types'
import type { CapsulesPreview } from '@/types/CapsulesTypes'
import type {
  CapsuleEditionKind,
  MainPageCapsule,
  MainPageCapsuleItem,
  MainPageCapsulesResponse,
  MainPageOutfit,
} from './capsules'

/* ——— Personalization of the home shelf ——— */

export interface CapsulesPersonalizationView {
  /** True only when the server applied the profile AND gave a match figure. */
  tuned: boolean
  /** 0..100 integer from the server's taste term; `null` when not tuned. */
  matchPercent: number | null
  editionKind: CapsuleEditionKind
}

export const CAPSULES_NOT_TUNED: CapsulesPersonalizationView = {
  tuned: false,
  matchPercent: null,
  editionKind: 'curated',
}

const EDITION_KINDS: ReadonlySet<string> = new Set(['curated', 'curated_reranked', 'personal'])

/**
 * The subtitle of "Capsules for you" comes from here and nowhere else. No
 * block, `applied=false`, `tuned=false` or a missing percent all read as
 * "Not tuned yet": a tuned line without a number would be a made-up number.
 */
export function resolveCapsulesPersonalization(
  response: Pick<MainPageCapsulesResponse, 'personalization'> | null | undefined,
): CapsulesPersonalizationView {
  const block = response?.personalization
  if (!block) return CAPSULES_NOT_TUNED

  const editionKind: CapsuleEditionKind = EDITION_KINDS.has(block.editionKind)
    ? block.editionKind
    : 'curated'

  const percent = block.matchPercent
  const hasPercent = typeof percent === 'number' && Number.isFinite(percent)
  if (!block.applied || !block.tuned || !hasPercent) {
    return { tuned: false, matchPercent: null, editionKind }
  }

  return {
    tuned: true,
    matchPercent: Math.min(100, Math.max(0, Math.round(percent))),
    editionKind,
  }
}

/** ML `type.id` — `outfit_composition.py::SINGLE_SLOT_TYPE_IDS` + accessories. */
export const OUTFIT_TYPE_ID = {
  top: 0,
  bottom: 1,
  shoes: 2,
  accessories: 3,
  fullbody: 4,
  outwear: 5,
} as const

export type OutfitTypeId = (typeof OUTFIT_TYPE_ID)[keyof typeof OUTFIT_TYPE_ID]

/** `main_category.id` of the handbag family (`_BAG_MAINS`) and the wallet (57). */
const BAG_MAIN_CATEGORY_IDS: ReadonlySet<number> = new Set([
  44, 45, 46, 47, 48, 49, 50, 51, 52, 53, 54, 55, 56, 57,
])

export function isBagMainCategory(mainCategoryId: number | null | undefined): boolean {
  return mainCategoryId != null && BAG_MAIN_CATEGORY_IDS.has(mainCategoryId)
}

export interface CapsuleAudienceQuery {
  gender: 'man' | 'woman' | 'unisex'
  age_group: 'babies' | 'kids' | 'teenagers' | 'adults'
}

/**
 * `?gender=&age_group=` from the header audience. The legacy route took
 * `?audience=`; the new one wants the two axes the capsule is seeded with.
 * Kids has no gender axis in the header, so it asks for unisex kids.
 */
export function audienceToCapsuleQuery(raw: unknown): CapsuleAudienceQuery {
  const audience: Audience = resolveAudience(raw)
  if (audience === 'kids') return { gender: 'unisex', age_group: 'kids' }
  return { gender: audience, age_group: 'adults' }
}

/** Card slot for one outfit piece; accessories take the first free acc slot. */
export function resolveFairCapsuleSlot(
  item: Pick<MainPageCapsuleItem, 'typeId' | 'mainCategoryId'>,
  taken: ReadonlySet<FairCapsuleItemType>,
): FairCapsuleItemType | null {
  switch (item.typeId) {
    case OUTFIT_TYPE_ID.top:
    case OUTFIT_TYPE_ID.fullbody:
      return 'top'
    case OUTFIT_TYPE_ID.bottom:
      return 'bottom'
    case OUTFIT_TYPE_ID.shoes:
      return 'shoes'
    case OUTFIT_TYPE_ID.outwear:
      return 'outerwear'
    case OUTFIT_TYPE_ID.accessories:
      if (isBagMainCategory(item.mainCategoryId)) return 'bag'
      if (!taken.has('accessory-1')) return 'accessory-1'
      if (!taken.has('accessory-2')) return 'accessory-2'
      return null
    default:
      return null
  }
}

/** One outfit → the card's slotted items; a slot is filled once, empties skipped. */
export function mapMainPageOutfitToFairItems(outfit: MainPageOutfit): FairCapsuleItem[] {
  const taken = new Set<FairCapsuleItemType>()
  const result: FairCapsuleItem[] = []
  for (const item of outfit.items ?? []) {
    if (!item.imageUrl) continue
    const slot = resolveFairCapsuleSlot(item, taken)
    if (!slot || taken.has(slot)) continue
    taken.add(slot)
    result.push({
      id: item.productId,
      type: slot,
      imageUrl: item.imageUrl,
      alt: item.name || slot,
      product: {
        id: item.productId,
        name: item.name ?? '',
        brand: item.brand ?? '',
        price: item.price?.gte ?? 0,
        variantId: item.variantId,
      },
    })
  }
  return result
}

/* ——— Legacy capsule detail page (`/capsule=:id`) shapes ——— */

/** CSS hooks the legacy carousel positions by (`capsules-costil.css`). */
export type LegacyCapsuleProductType =
  | 'outerwear'
  | 'top'
  | 'bottom'
  | 'footwear'
  | 'bag'
  | 'accessory_1'
  | 'accessory_2'

const LEGACY_BY_FAIR_SLOT: Record<FairCapsuleItemType, LegacyCapsuleProductType> = {
  outerwear: 'outerwear',
  top: 'top',
  bottom: 'bottom',
  shoes: 'footwear',
  bag: 'bag',
  'accessory-1': 'accessory_1',
  'accessory-2': 'accessory_2',
}

/**
 * A catalogue picture inside CSS `url()`. The CDN paths carry the product name
 * with its spaces (`…/farfetch/ribbed logo-patch jumper/…`), and an unquoted
 * `url()` with whitespace is invalid CSS — the browser drops the declaration and
 * the look canvas stays blank. Quoted, with the quote and backslash escaped.
 */
export function cssUrl(url: string): string {
  return `url("${url.replace(/\\/g, '%5C').replace(/"/g, '%22').replace(/\n/g, '%0A')}")`
}

export function mapMainPageItemToLegacyCapsuleItem(
  item: MainPageCapsuleItem,
  productType?: LegacyCapsuleProductType,
): CapsuleItem {
  return {
    id: item.productId,
    variant_id: item.variantId,
    product_type: productType,
    name: item.name ?? '',
    brand: item.brand ?? '',
    price: { gte: item.price?.gte ?? 0, lte: item.price?.lte ?? 0 },
    discount: null,
    currency: item.currency ?? 'USD',
    is_favorite: false,
    image_urls: item.imageUrl ? [item.imageUrl] : [],
  }
}

export function mapMainPageOutfitToLegacyCapsuleData(
  outfit: MainPageOutfit,
  capsuleId: number,
): CapsuleData {
  const taken = new Set<FairCapsuleItemType>()
  const capsuleItems: CapsuleItem[] = []
  for (const item of outfit.items ?? []) {
    const slot = resolveFairCapsuleSlot(item, taken)
    if (slot) taken.add(slot)
    capsuleItems.push(
      mapMainPageItemToLegacyCapsuleItem(item, slot ? LEGACY_BY_FAIR_SLOT[slot] : undefined),
    )
  }
  return { capsule_id: capsuleId, capsule_items: capsuleItems }
}

export function buildLegacyCapsule(
  meta: Pick<MainPageCapsule, 'id' | 'name' | 'description'>,
  outfits: readonly MainPageOutfit[],
): Capsule {
  return {
    id: meta.id,
    name: meta.name,
    description: meta.description ?? '',
    items: outfits.map((outfit) => mapMainPageOutfitToLegacyCapsuleData(outfit, meta.id)),
  }
}

/* ——— Legacy home carousel (`CapsulesWrapper` / `CustomCapsuleComponent`) ——— */

/** `GET /capsules` → the legacy preview shape the old home carousel positions by. */
export function mapMainPageCapsulesToLegacyPreview(
  response: MainPageCapsulesResponse | null | undefined,
): CapsulesPreview[] {
  return (response?.items ?? []).map((capsule) => ({
    id: capsule.id,
    name: capsule.name,
    description: capsule.description ?? '',
    items: (capsule.outfits ?? []).map((outfit) => {
      const taken = new Set<FairCapsuleItemType>()
      return {
        capsuleId: capsule.id,
        capsuleItems: (outfit.items ?? []).map((item) => {
          const slot = resolveFairCapsuleSlot(item, taken)
          if (slot) taken.add(slot)
          return {
            productId: item.productId,
            productType: slot ? LEGACY_BY_FAIR_SLOT[slot] : '',
            productVariantId: item.variantId,
            productName: item.name ?? '',
            imageUrl: item.imageUrl,
            brandName: item.brand ?? '',
            price: item.price?.gte ?? 0,
          }
        }),
      }
    }),
  }))
}
